import type { ReactElement } from 'react';
import type { IntlShape } from 'react-intl';
import { FormattedMessage } from '@/components/formatted_message/FormattedMessage';
import { find } from 'ramda';
import { isObject } from '@enmon/commons/lib/utils';
import type { ZodError } from 'zod';

export interface FormError {
  path: Array<string | number | symbol>;
  type: string;
  message?: string;
  context?: Record<string, unknown>;
  fields?: string[];
}
/**
  @deprecated should be part of utils in commons
 */
const isStringOrNumber = (value: unknown): value is string | number =>
  typeof value === 'string' || typeof value === 'number';

/**
 * Use to display validation errors from Zod schema in forms.
 */
export const mapZodIssueToFormError = (issue: ZodError['issues'][number]): FormError => {
  const { path, code, message, ...rest } = issue;

  return {
    path,
    type: code,
    message,
    context: rest,
  };
};

/**
 * Check if passed value is FormError type.
 */
export function isFormError(value: unknown): value is FormError {
  if (!isObject(value)) return false;
  const { message, path, type, context } = value as Record<keyof FormError, unknown>;

  return (
    Array.isArray(path) &&
    path.every(isStringOrNumber) &&
    typeof type === 'string' &&
    (message === undefined || typeof message === 'string') &&
    (context === undefined || isObject(context))
  );
}

export const mapZodIssuesToFormErrors = (zodIssues: ZodError['issues']): FormError[] => {
  return zodIssues.map(issue => ({
    path: issue.path,
    type: issue.code,
    message: issue.message,
  }));
};

export interface GetFieldErrorOptions {
  /** Form name */
  form: string;
  /** Field name */
  name: string;

  formErrors: readonly FormError[];
  intl?: IntlShape;
  values?: Record<string, string | number | boolean | null | undefined | Date>;
}

/**
 * @deprecatd Use `useFormErrors` hook instead.
 */
export const getFieldError = ({
  form,
  name,
  formErrors,
  intl,
  values,
}: GetFieldErrorOptions): undefined | string | ReactElement => {
  const fieldError = find(error => error.path.join('.') === name, formErrors);
  if (!fieldError) return undefined;

  const id = `${form}.${fieldError.path.join('.')}.${fieldError.type}`;

  if (intl) {
    return intl.formatMessage(
      {
        id,
        defaultMessage: fieldError.message,
      },
      values,
    );
  }

  return <FormattedMessage defaultMessage={fieldError.message} id={id} />;
};
